<script lang="ts">
	import RouteFeatureStructure from '$lib/components/city/feature/RouteFeatureStructure.svelte';
	import { createDefaultPlayerState, type TourPlayerState } from '$lib/components/ui/map/_other/tourPlayerState';
	import { TourWikiCache, type TourWikiCacheSnapshot } from '$lib/components/ui/map/_other/tourWikiCache';
	import { DIFFICULTY_METAS } from '$lib/features/_other/difficulty.enum';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { getSurfaceBreakdown } from '$lib/features/route/routeStep/roadSurface.enum';
	import type { ScenicBikeRide } from '$lib/features/scenicBikeRide/scenicBikeRide.type';
	import { findFirstTourStopImage, type Tour } from '$lib/features/tour/tour.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import { buildSurfacePhrase, firstThatFits, META_DESCRIPTION_MAX, META_TITLE_MAX, rankStops } from '$lib/utils/meta.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Snapshot } from '@sveltejs/kit';
	import type { Output } from './+page.server';

	const featureType = CityFeatureType.SCENIC_BIKE_RIDE;

	const { data }: { data: { output: Output } } = $props();

	let player = $state<TourPlayerState>(createDefaultPlayerState());

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const item = $derived<ScenicBikeRide>(output.item);
	const tour = $derived<Tour>(item.tour);
	const slug = $derived<string>(tour.feature.slug);
	const wikiCache = $derived<TourWikiCache>(new TourWikiCache(slug));
	const meta = $derived.by(() => {
		const listFormatter = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' });
		const metaImage: ImageDetails | undefined = findFirstTourStopImage(tour);
		const cityName = location.city.label;
		const routeName = tour.feature.englishName;

		const distanceKm = Math.round(tour.route.distanceMeters / 100) / 10;
		const totalMinutes = Math.round(tour.route.durationSeconds / 60);
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const duration = hours === 0 ? `${minutes} min` : minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;

		const category = item.categories[0]?.toLowerCase();
		const difficulty = DIFFICULTY_METAS[item.difficulty].label.toLowerCase();
		const surfaceBreakdown = getSurfaceBreakdown(data.output.item.tour.route.segments);
		const surfacePhrase = buildSurfacePhrase(surfaceBreakdown);

		const title = firstThatFits(
			[`${cityName} Cycling Route: ${routeName} (${distanceKm} km)`, `${cityName} Cycling Route: ${routeName}`],
			META_TITLE_MAX
		);

		const descriptor = [difficulty, category].filter(Boolean).join(' ');
		const details = [duration, surfacePhrase].filter(Boolean).join(', ');
		const lead = `${distanceKm} km ${descriptor} cycling route in ${cityName}: ${details}`;

		const ranked = rankStops(tour.stops);
		const closers = [' Map, elevation and tips.', ' Map and tips.', ''];
		const candidates: string[] = [];
		for (let count = Math.min(3, ranked.length); count >= 0; count--) {
			const names = ranked
				.slice(0, count)
				.sort((a, b) => a.index - b.index)
				.map((s) => s.name);
			const stops = count > 0 ? ` Passes ${listFormatter.format(names)}.` : '';
			for (const closer of closers) candidates.push(`${lead}.${stops}${closer}`);
		}
		const description = firstThatFits(candidates, META_DESCRIPTION_MAX);

		return {
			title,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeature({ location, type: tour.feature.type, featureSlug: tour.feature.slug }).href}`,
			imageUrl: metaImage ? UrlUtil.getFullImageUrl(metaImage) : undefined
		};
	});
	const jsonLdTouristTrip = $derived.by(() => {
		const json = data.output.jsonLdTouristTrip;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdTouristTrip]);

	$effect(() => {
		slug;
		player = createDefaultPlayerState();
	});

	export const snapshot: Snapshot<{ player: TourPlayerState; wiki: TourWikiCacheSnapshot }> = {
		capture: () => ({
			player: $state.snapshot(player),
			wiki: wikiCache.serialize()
		}),
		restore: (value) => {
			wikiCache.hydrate(value.wiki);
			player = { ...value.player, isPlaying: false };
		}
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />
	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<RouteFeatureStructure
	{featureType}
	data={item}
	{location}
	cityHeroImage={output.cityHeroImage}
	language={output.countryLanguage}
	proTipsSectionTitle="Rider's Notebook"
	breadcrumbs={data.output.breadcrumbs}
	otherRoutes={output.otherRoutes}
	otherTypesOfRoutes={output.otherTypesOfRoutes}
	bind:player
	{wikiCache}
	onExtraStats={() => {
		const meta = DIFFICULTY_METAS[item.difficulty];
		return [
			{
				label: 'Intensity',
				Icon: meta.Icon,
				value: meta.label
			}
		];
	}}
/>
